import React, { useState, useEffect } from 'react';
import './Treasury.css';
import { saveTreasuryTransaction, getAllTreasuryTransactions } from '../../../services/firestoreService';

const Treasury = () => {
  const [treasuryBalance, setTreasuryBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [newEntry, setNewEntry] = useState({
    type: 'credit',
    amount: '',
    reason: ''
  });
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load treasury data from Firestore and localStorage on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to load from Firestore first
        const firestoreTransactions = await getAllTreasuryTransactions();
        if (firestoreTransactions.length > 0) {
          setTransactions(firestoreTransactions);
          calculateBalance(firestoreTransactions);
          // Update localStorage with Firestore data
          localStorage.setItem('treasuryTransactions', JSON.stringify(firestoreTransactions));
        } else {
          // Fallback to localStorage if Firestore is empty
          const savedTransactions = localStorage.getItem('treasuryTransactions');
          if (savedTransactions) {
            const parsed = JSON.parse(savedTransactions);
            setTransactions(parsed);
            calculateBalance(parsed);
          }
        }
      } catch (error) {
        console.error('Error loading treasury data:', error);
        // Fallback to localStorage on error
        const savedTransactions = localStorage.getItem('treasuryTransactions');
        if (savedTransactions) {
          const parsed = JSON.parse(savedTransactions);
          setTransactions(parsed);
          calculateBalance(parsed);
        }
      }
    };
    
    loadData();
  }, []);

  // Save transactions to localStorage whenever they change
  useEffect(() => {
    if (transactions.length > 0) {
      localStorage.setItem('treasuryTransactions', JSON.stringify(transactions));
    }
  }, [transactions]);

  const calculateBalance = (txns) => {
    const balance = txns.reduce((total, transaction) => {
      const amount = parseFloat(transaction.amount) || 0;
      return transaction.type === 'credit' ? total + amount : total - amount;
    }, 0);
    setTreasuryBalance(balance);
  };

  const handleAddEntry = (e) => {
    e.preventDefault();

    if (!newEntry.amount || parseFloat(newEntry.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!newEntry.reason.trim()) {
      alert('Please enter a reason/description');
      return;
    }

    const transaction = {
      id: Date.now(),
      type: newEntry.type,
      amount: parseFloat(newEntry.amount),
      reason: newEntry.reason.trim(),
      date: new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      }),
      timestamp: Date.now()
    };

    const updatedTransactions = [...transactions, transaction];
    setTransactions(updatedTransactions);
    calculateBalance(updatedTransactions);

    // Save to Firestore
    saveTreasuryTransaction(transaction);

    // Reset form
    setNewEntry({
      type: 'credit',
      amount: '',
      reason: ''
    });
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction({ ...transaction });
    setIsEditing(true);
    setNewEntry({
      type: transaction.type,
      amount: transaction.amount.toString(),
      reason: transaction.reason
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();

    if (!newEntry.amount || parseFloat(newEntry.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!newEntry.reason.trim()) {
      alert('Please enter a reason/description');
      return;
    }

    try {
      const updatedTransaction = {
        ...editingTransaction,
        type: newEntry.type,
        amount: parseFloat(newEntry.amount),
        reason: newEntry.reason.trim(),
        updatedAt: new Date().toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          dateStyle: 'medium',
          timeStyle: 'short'
        })
      };

      // Update in local state
      const updatedTransactions = transactions.map(t =>
        t.id === editingTransaction.id ? updatedTransaction : t
      );
      setTransactions(updatedTransactions);
      calculateBalance(updatedTransactions);

      // Update in Firestore
      const { updateTreasuryTransaction } = await import('../../../services/firestoreService');
      await updateTreasuryTransaction(editingTransaction.id, updatedTransaction);

      // Reset form
      setIsEditing(false);
      setEditingTransaction(null);
      setNewEntry({
        type: 'credit',
        amount: '',
        reason: ''
      });

      alert('Transaction updated successfully!');
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTransaction(null);
    setNewEntry({
      type: 'credit',
      amount: '',
      reason: ''
    });
  };



  return (
    <div className="treasury-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-university"></i> Treasury Management</h1>
          <p>Track and manage your treasury transactions</p>
        </div>
      </div>

      {/* Treasury Balance Card */}
      <div className="treasury-balance-card">
        <div className="balance-icon">
          <i className="fas fa-wallet"></i>
        </div>
        <div className="balance-content">
          <h2>Total Available Balance</h2>
          <div className={`balance-amount ${treasuryBalance >= 0 ? 'positive' : 'negative'}`}>
            ₹{Math.abs(treasuryBalance).toFixed(2)}
          </div>
          <span className="balance-status">
            {treasuryBalance >= 0 ? 'Surplus' : 'Deficit'}
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      {transactions.length > 0 && (
        <div className="treasury-summary">
          <div className="summary-card">
            <div className="summary-icon credit">
              <i className="fas fa-arrow-down"></i>
            </div>
            <div className="summary-content">
              <span className="summary-label">Total Credits</span>
              <span className="summary-value credit">
                ₹{transactions
                  .filter(t => t.type === 'credit')
                  .reduce((sum, t) => sum + t.amount, 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon debit">
              <i className="fas fa-arrow-up"></i>
            </div>
            <div className="summary-content">
              <span className="summary-label">Total Debits</span>
              <span className="summary-value debit">
                ₹{transactions
                  .filter(t => t.type === 'debit')
                  .reduce((sum, t) => sum + t.amount, 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="summary-content">
              <span className="summary-label">Total Transactions</span>
              <span className="summary-value">{transactions.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Entry Form */}
      <div className="treasury-form-card">
        <h3>
          <i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus-circle'}`}></i> 
          {isEditing ? 'Edit Transaction' : 'Add New Entry'}
        </h3>
        <form onSubmit={isEditing ? handleUpdateTransaction : handleAddEntry} className="treasury-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="entry-type">
                <i className="fas fa-exchange-alt"></i> Transaction Type
              </label>
              <select
                id="entry-type"
                value={newEntry.type}
                onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
                className="form-select"
              >
                <option value="credit">Credit (Money In)</option>
                <option value="debit">Debit (Money Out)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="entry-amount">
                <i className="fas fa-rupee-sign"></i> Amount
              </label>
              <input
                type="number"
                id="entry-amount"
                step="0.01"
                min="0.01"
                value={newEntry.amount}
                onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                placeholder="Enter amount"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="entry-reason">
              <i className="fas fa-comment-alt"></i> Reason / Description
            </label>
            <textarea
              id="entry-reason"
              value={newEntry.reason}
              onChange={(e) => setNewEntry({ ...newEntry, reason: e.target.value })}
              placeholder="Enter reason or description for this transaction"
              className="form-textarea"
              rows="3"
              required
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-add-entry">
              <i className="fas fa-check"></i> {isEditing ? 'Update Transaction' : 'Add Entry'}
            </button>
            {isEditing && (
              <button type="button" className="btn-cancel-edit" onClick={handleCancelEdit}>
                <i className="fas fa-times"></i> Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Transaction History */}
      <div className="treasury-history-card">
        <div className="history-header">
          <h3><i className="fas fa-history"></i> Transaction History</h3>
        </div>

        {transactions.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No transactions yet</p>
            <span>Add your first entry to start tracking treasury</span>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="transactions-table-container desktop-view">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Balance After</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...transactions].reverse().map((transaction, index) => {
                    // Calculate balance up to this transaction
                    const transactionIndex = transactions.length - 1 - index;
                    const balanceAfter = transactions
                      .slice(0, transactionIndex + 1)
                      .reduce((total, txn) => {
                        const amount = parseFloat(txn.amount) || 0;
                        return txn.type === 'credit' ? total + amount : total - amount;
                      }, 0);

                    return (
                      <tr key={transaction.id}>
                        <td className="date-cell">
                          <i className="fas fa-calendar-alt"></i>
                          {transaction.date}
                        </td>
                        <td>
                          <span className={`type-badge ${transaction.type}`}>
                            <i className={`fas ${transaction.type === 'credit' ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                            {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                          </span>
                        </td>
                        <td className={`amount-cell ${transaction.type}`}>
                          {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount.toFixed(2)}
                        </td>
                        <td className="reason-cell">{transaction.reason}</td>
                        <td className={`balance-cell ${balanceAfter >= 0 ? 'positive' : 'negative'}`}>
                          ₹{Math.abs(balanceAfter).toFixed(2)}
                        </td>
                        <td className="actions-cell">
                          <button 
                            className="btn-edit-transaction" 
                            onClick={() => handleEditTransaction(transaction)}
                            title="Edit transaction"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="transactions-mobile-view mobile-view">
              {[...transactions].reverse().map((transaction, index) => {
                // Calculate balance up to this transaction
                const transactionIndex = transactions.length - 1 - index;
                const balanceAfter = transactions
                  .slice(0, transactionIndex + 1)
                  .reduce((total, txn) => {
                    const amount = parseFloat(txn.amount) || 0;
                    return txn.type === 'credit' ? total + amount : total - amount;
                  }, 0);

                return (
                  <div key={transaction.id} className="transaction-mobile-card">
                    <div className="transaction-mobile-header">
                      <span className={`type-badge ${transaction.type}`}>
                        <i className={`fas ${transaction.type === 'credit' ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                        {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                      </span>
                      <span className={`amount-cell ${transaction.type}`}>
                        {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="transaction-mobile-body">
                      <div className="transaction-mobile-row">
                        <i className="fas fa-comment-alt"></i>
                        <span>{transaction.reason}</span>
                      </div>
                      <div className="transaction-mobile-row">
                        <i className="fas fa-calendar-alt"></i>
                        <span>{transaction.date}</span>
                      </div>
                      <div className="transaction-mobile-row">
                        <i className="fas fa-wallet"></i>
                        <span>Balance: <strong className={balanceAfter >= 0 ? 'positive' : 'negative'}>
                          ₹{Math.abs(balanceAfter).toFixed(2)}
                        </strong></span>
                      </div>
                    </div>
                    <div className="transaction-mobile-footer">
                      <button 
                        className="btn-edit-transaction" 
                        onClick={() => handleEditTransaction(transaction)}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default Treasury;
